import { useEffect, useMemo, useRef, useState } from 'react'
import { getDashaTree, getDashaNow, findRunningPath } from '@astro/vedastro'
import { Badge, PLANET_GLYPHS, StudioError, TableSkeleton } from '../StudioBits'

const NATURE_TONE = { Good: 'green', Bad: 'red' }
const NATURE_LABEL = { Good: 'Favourable', Bad: 'Challenging' }

function fmtDate(d) {
  return d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'
}

function DashaNode({ node, now, depth }) {
  const running = node.start && node.end && node.start <= now && now < node.end
  // The running Maha dasha starts expanded so "today" is one glance away.
  const [open, setOpen] = useState(running && depth === 1)
  const expandable = node.subs.length > 0

  return (
    <div className={`ks-dasha-node ks-dasha-l${depth}`}>
      <button
        type="button"
        className={`ks-dasha-row${running ? ' running' : ''}${expandable ? '' : ' leaf'}`}
        onClick={() => expandable && setOpen((o) => !o)}
        aria-expanded={expandable ? open : undefined}
      >
        <span className="ks-dasha-caret" aria-hidden="true">{expandable ? (open ? '▾' : '▸') : '·'}</span>
        <span className="ks-planet-cell">
          <span className="ks-planet-glyph">{PLANET_GLYPHS[node.lord] || '✦'}</span>
          {node.lord}
        </span>
        <span className="ks-dasha-level">{node.levelLabel}</span>
        <span className="ks-dasha-dates ks-mono">{fmtDate(node.start)} → {fmtDate(node.end)}</span>
        <span className="ks-dasha-dur">{node.duration}</span>
        <span className="ks-badges">
          {NATURE_TONE[node.nature] && <Badge tone={NATURE_TONE[node.nature]}>{NATURE_LABEL[node.nature]}</Badge>}
          {running && <Badge tone="gold">Running</Badge>}
        </span>
      </button>
      {open && (
        <div className="ks-dasha-children">
          {depth < 3 && node.description && <p className="ks-dasha-desc">{node.description}</p>}
          {node.subs.map((s) => (
            <DashaNode key={`${s.lord}-${s.start?.getTime()}`} node={s} now={now} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Vimshottari dashas — the full 120-year Maha → Antar → Pratyantar tree from
 * one cached call, with the currently-running chain summarised on top.
 */
export default function DashasPart({ birth }) {
  const [status, setStatus] = useState('loading') // loading | error | done
  const [tree, setTree] = useState(null)
  const [quickChain, setQuickChain] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const reqRef = useRef(0)
  const now = useMemo(() => new Date(), [])

  const load = () => {
    const my = ++reqRef.current
    setStatus('loading')
    const args = { dob: birth.dob, tob: birth.tob, location: birth.location }
    // Sub-second call — puts the running chain on screen a beat before the
    // full 120-year tree (computed locally, gated only on one shared
    // planet-data call) finishes.
    getDashaNow(args)
      .then((chain) => {
        if (my === reqRef.current) setQuickChain(chain)
      })
      .catch(() => { /* the full tree still provides the chain */ })
    getDashaTree(args)
      .then((t) => {
        if (my !== reqRef.current) return
        setTree(t)
        setStatus('done')
      })
      .catch((err) => {
        if (my !== reqRef.current || err?.name === 'AbortError') return
        setErrorMsg(err?.message || 'We could not compute the dashas right now. Please try again.')
        setStatus('error')
      })
  }
  useEffect(load, [birth]) // eslint-disable-line react-hooks/exhaustive-deps

  const running = useMemo(() => (tree ? findRunningPath(tree, now) : []), [tree, now])
  // Dated chain once the tree is in; the quick lords-only chain meanwhile.
  const chain = running.length ? running : quickChain || []

  return (
    <div>
      {chain.length > 0 && (
        <div className="ks-dasha-now">
          <span className="ks-dasha-now-label">Running now</span>
          <div className="ks-dasha-now-chain">
            {chain.map((n, i) => (
              <span key={n.levelLabel} className="ks-dasha-now-item">
                {i > 0 && <span className="ks-dasha-now-sep">›</span>}
                <strong>{PLANET_GLYPHS[n.lord] || '✦'} {n.lord}</strong> {n.levelLabel}
                {n.end && <span className="ks-dim"> till {fmtDate(n.end)}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {status === 'loading' && <TableSkeleton rows={9} cols={5} />}
      {status === 'error' && <StudioError text={errorMsg} onRetry={load} />}
      {status === 'done' && tree && (
        <>
          <div className="ks-dasha-tree">
            {tree.map((n) => (
              <DashaNode key={`${n.lord}-${n.start?.getTime()}`} node={n} now={now} depth={1} />
            ))}
          </div>
          <p className="ks-note">
            Vimshottari periods for the full 120-year cycle, timed from your Moon nakshatra. Expand any Maha dasha
            to see its Antar dashas (bhukti), and those again for Pratyantar periods.
          </p>
        </>
      )}
    </div>
  )
}
